import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, CloudArrowDown, CircleNotch, Sparkle, PaperPlaneTilt, Envelope, ChartLine, Receipt } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TABS = ["conferences", "speakers", "papers"];

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("conferences");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);

  async function load() {
    const url = tab === "conferences" ? "/conferences/all" : `/${tab}`;
    const { data } = await api.get(url);
    setItems(Array.isArray(data) ? data : (data.items || []));
  }
  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [tab, user]);

  async function syncOpenAlex() {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-openalex?limit=50");
      toast.success(`Imported ${data.added} new conferences from OpenAlex (${data.skipped} skipped)`);
      if (tab === "conferences") load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "OpenAlex sync failed");
    } finally {
      setSyncing(false);
    }
  }

  async function importFromUrl() {
    const url = window.prompt("Paste a conference website URL — Claude will extract the metadata:");
    if (!url) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/import-url", { url });
      // Open the edit form pre-filled with the extracted data
      setEditing({
        title: data.title || "",
        description: data.description || "",
        topic: data.topic || "General",
        type: data.type || "academic",
        location_name: data.location_name || "",
        city: data.city || "",
        country: data.country || "",
        lat: 0, lng: 0,
        start_date: data.start_date || "",
        end_date: data.end_date || "",
        website: data.website || url,
        image_url: "",
        issn: [],
        host_organization: "",
        cfp_deadline: data.cfp_deadline || "",
        cfp_url: data.cfp_url || "",
        notification_date: "",
        early_bird_date: "",
        featured: false,
        tags: data.tags || [],
        speaker_ids: [],
        paper_ids: [],
      });
      toast.success("Form pre-filled — review and Save");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setSyncing(false); }
  }

  async function sendTestDigest() {
    const target = window.prompt("Send a test digest to which email? (leave blank to send to your own)", "") || "";
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/send-test-email", target ? { to: target } : {});
      toast.success(`Test digest sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed (verify domain in Resend or send to a verified email)");
    } finally { setSyncing(false); }
  }

  async function runDigest() {
    if (!confirm("Run the weekly digest job NOW for all subscribers with bookmarks? This will send real emails.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/send-digest");
      toast.success(`Digest job done: ${data.sent} sent · ${data.failed || 0} failed (topic: ${data.topic || '—'})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Digest run failed");
    } finally { setSyncing(false); }
  }

  if (loading) return <div className="px-6 md:px-10 lg:px-16 py-16">Loading…</div>;
  if (!user || user.role !== "admin") {
    return (
      <div data-testid="admin-denied" className="px-6 md:px-10 lg:px-16 py-16 max-w-md mx-auto text-center">
        <h1 className="font-display font-bold text-4xl mb-3">Admin only</h1>
        <p className="text-gray-600 mb-6">Sign in with an admin account to manage the catalog.</p>
        <Link to="/login" className="btn-primary">Sign in</Link>
      </div>
    );
  }

  async function remove(id) {
    if (!confirm("Delete this item?")) return;
    try { await api.delete(`/${tab}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  }

  return (
    <div data-testid="admin-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
      <div className="overline mb-3">Admin · Manage</div>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-display font-bold text-5xl md:text-6xl tracking-tighter leading-none">Catalog Admin</h1>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/analytics" data-testid="admin-analytics-link" className="btn-secondary text-sm" style={{padding:".55rem 1rem"}}>
            <ChartLine size={14} weight="duotone" /> Analytics
          </Link>
          <Link to="/admin/payments" data-testid="admin-payments-link" className="btn-secondary text-sm" style={{padding:".55rem 1rem"}}>
            <Receipt size={14} weight="duotone" /> Payments
          </Link>
        </div>
      </div>

      <div className="border-b-2 border-black flex gap-6">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => { setTab(t); setEditing(null); }}
            className={`pb-3 -mb-px text-sm uppercase font-medium tracking-wider border-b-2 ${tab === t ? "border-black text-black" : "border-transparent text-gray-500 hover:text-black"}`}
          >
            {t}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {tab === "conferences" && (
            <>
              <button data-testid="admin-import-url" onClick={importFromUrl} disabled={syncing} className="btn-secondary text-sm" style={{padding:'.5rem 1rem'}}>
                {syncing ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} weight="duotone" />}
                Import from URL
              </button>
              <button data-testid="admin-sync-openalex" onClick={syncOpenAlex} disabled={syncing} className="btn-secondary text-sm" style={{padding:'.5rem 1rem'}}>
                {syncing ? <CircleNotch size={14} className="animate-spin" /> : <CloudArrowDown size={14} />}
                Sync OpenAlex
              </button>
            </>
          )}
          <button data-testid="admin-test-email" onClick={sendTestDigest} disabled={syncing} className="btn-secondary text-sm" style={{padding:'.5rem 1rem'}}>
            <Envelope size={14} /> Send test digest
          </button>
          <button data-testid="admin-run-digest" onClick={runDigest} disabled={syncing} className="btn-secondary text-sm" style={{padding:'.5rem 1rem'}}>
            <PaperPlaneTilt size={14} /> Run digest now
          </button>
          <button data-testid="admin-add-btn" onClick={() => setEditing(emptyFor(tab))} className="btn-accent text-sm" style={{padding:'.5rem 1rem'}}>
            <Plus size={16} /> New {tab.slice(0, -1)}
          </button>
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-12 gap-8">
        <div className={editing ? "md:col-span-7" : "md:col-span-12"}>
          <div className="border border-gray-200">
            {items.length === 0 && <div className="p-10 text-center text-gray-500">No items.</div>}
            {items.map((it, i) => (
              <div key={it.id} data-testid={`admin-row-${i}`} className="flex items-center gap-3 border-b border-gray-200 last:border-0 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.title || it.name}</div>
                  <div className="text-xs text-gray-500 truncate">{it.city || it.organization || (it.authors || []).join(", ")}</div>
                </div>
                <button onClick={() => setEditing(it)} data-testid={`admin-edit-${i}`} className="p-2 hover:bg-gray-100"><PencilSimple size={16} /></button>
                <button onClick={() => remove(it.id)} data-testid={`admin-delete-${i}`} className="p-2 hover:bg-gray-100 text-red-600"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        {editing && (
          <div className="md:col-span-5">
            <EditForm tab={tab} initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
          </div>
        )}
      </div>
    </div>
  );
}

function emptyFor(tab) {
  if (tab === "conferences") return { title: "", description: "", topic: "General", type: "academic", location_name: "", city: "", country: "", lat: 0, lng: 0, start_date: "", end_date: "", website: "", image_url: "", issn: [], host_organization: "", cfp_deadline: "", cfp_url: "", notification_date: "", early_bird_date: "", featured: false, tags: [], speaker_ids: [], paper_ids: [] };
  if (tab === "speakers") return { name: "", title: "", organization: "", bio: "", image_url: "", website: "", twitter: "" };
  return { title: "", abstract: "", authors: [], track: "", pdf_url: "", conference_id: null };
}

function EditForm({ tab, initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [fetchingProc, setFetchingProc] = useState(false);
  const isNew = !initial.id;

  function setF(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function setProc(k, v) { setForm((f) => ({ ...f, proceedings: { ...(f.proceedings || {}), [k]: v } })); }

  async function fetchProceedings() {
    if (!initial.id) { toast.error("Save the conference first"); return; }
    setFetchingProc(true);
    try {
      const { data } = await api.post(`/admin/conferences/${initial.id}/fetch-proceedings?limit=10`);
      toast.success(`Imported ${data.imported_papers} papers · publisher: ${data.proceedings.publisher || "—"}`);
      setForm((f) => ({ ...f, proceedings: data.proceedings }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch proceedings");
    } finally { setFetchingProc(false); }
  }

  async function save() {
    setBusy(true);
    try {
      const payload = { ...form };
      // ensure tags / authors arrays
      if (typeof payload.tags === "string") payload.tags = payload.tags.split(",").map((s) => s.trim()).filter(Boolean);
      if (typeof payload.issn === "string") payload.issn = payload.issn.split(",").map((s) => s.trim()).filter(Boolean);
      if (typeof payload.authors === "string") payload.authors = payload.authors.split(",").map((s) => s.trim()).filter(Boolean);
      if (typeof payload.speaker_ids === "string") payload.speaker_ids = payload.speaker_ids.split(",").map((s) => s.trim()).filter(Boolean);
      if (typeof payload.paper_ids === "string") payload.paper_ids = payload.paper_ids.split(",").map((s) => s.trim()).filter(Boolean);
      if ("lat" in payload) payload.lat = Number(payload.lat) || 0;
      if ("lng" in payload) payload.lng = Number(payload.lng) || 0;
      delete payload.id;
      delete payload.created_at;

      if (isNew) {
        await api.post(`/${tab}`, payload);
        toast.success("Created");
      } else {
        await api.put(`/${tab}/${initial.id}`, payload);
        toast.success("Updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail ? JSON.stringify(e.response.data.detail) : "Save failed");
    } finally { setBusy(false); }
  }

  const fields = tab === "conferences"
    ? [["title", "text"], ["description", "textarea"], ["topic", "text"], ["type", "select:academic,tech,both"], ["location_name", "text"], ["city", "text"], ["country", "text"], ["lat", "number"], ["lng", "number"], ["start_date", "date"], ["end_date", "date"], ["cfp_deadline", "date"], ["cfp_url", "text"], ["website", "text"], ["image_url", "text"], ["issn", "csv"], ["host_organization", "text"], ["tags", "csv"], ["speaker_ids", "csv"], ["paper_ids", "csv"]]
    : tab === "speakers"
      ? [["name", "text"], ["title", "text"], ["organization", "text"], ["bio", "textarea"], ["image_url", "text"], ["website", "text"], ["twitter", "text"]]
      : [["title", "text"], ["abstract", "textarea"], ["authors", "csv"], ["track", "text"], ["pdf_url", "text"], ["conference_id", "text"]];

  const proceedings = form.proceedings || {};
  const showProceedings = tab === "conferences" && (form.type === "academic" || form.type === "both");

  return (
    <div data-testid="admin-edit-form" className="border-2 border-black p-6 sticky top-24">
      <div className="overline mb-3">{isNew ? "New" : "Edit"} {tab.slice(0, -1)}</div>
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
        {fields.map(([k, t]) => (
          <div key={k}>
            <label className="overline block mb-1">{k.replace(/_/g, " ")}</label>
            {t === "textarea" ? (
              <textarea data-testid={`form-${k}`} value={form[k] || ""} onChange={(e) => setF(k, e.target.value)} className="input-box min-h-[100px]" />
            ) : t.startsWith("select:") ? (
              <select data-testid={`form-${k}`} value={form[k] || ""} onChange={(e) => setF(k, e.target.value)} className="input-box">
                {t.split(":")[1].split(",").map((o) => (<option key={o} value={o}>{o}</option>))}
              </select>
            ) : t === "csv" ? (
              <input data-testid={`form-${k}`} value={Array.isArray(form[k]) ? form[k].join(", ") : form[k] || ""} onChange={(e) => setF(k, e.target.value)} className="input-box" placeholder="comma, separated" />
            ) : (
              <input data-testid={`form-${k}`} type={t} value={form[k] ?? ""} onChange={(e) => setF(k, e.target.value)} className="input-box" />
            )}
          </div>
        ))}

        {showProceedings && (
          <div className="border-t border-zinc-200 pt-4 mt-2">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Proceedings</div>
              {!isNew && (
                <button type="button" onClick={fetchProceedings} disabled={fetchingProc} data-testid="admin-fetch-proceedings" className="btn-secondary text-xs" style={{padding:'.4rem .7rem'}}>
                  {fetchingProc ? "Fetching…" : "Fetch from OpenAlex"}
                </button>
              )}
            </div>
            {[["publisher", "text"], ["doi", "text"], ["isbn", "text"], ["volume", "text"], ["url", "text"]].map(([pk, pt]) => (
              <div key={pk} className="mb-3">
                <label className="overline block mb-1">{pk}</label>
                <input data-testid={`form-proc-${pk}`} type={pt} value={proceedings[pk] ?? ""} onChange={(e) => setProc(pk, e.target.value)} className="input-box" />
              </div>
            ))}
            {proceedings.indexed_papers_count > 0 && (
              <div className="text-xs font-mono text-zinc-500">{proceedings.indexed_papers_count} indexed papers</div>
            )}
          </div>
        )}
      </div>
      <div className="mt-6 flex gap-3">
        <button data-testid="admin-save-btn" onClick={save} disabled={busy} className="btn-primary flex-1 justify-center">{busy ? "Saving…" : "Save"}</button>
        <button data-testid="admin-cancel-btn" onClick={onCancel} className="btn-secondary">Cancel</button>
      </div>
    </div>
  );
}
